import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './ui';

/**
 * Reiter mit Inhalt: `role="tablist"`/`tab`/`tabpanel`, Pfeiltasten, Pos1 und Ende.
 *
 * Alle Inhalte bleiben gemountet; nicht gewaehlte sind nur ausgeblendet (`hidden`). So behalten
 * Eingaben, der Skizzeneditor samt Rueckgaengig-Verlauf und der Zoom ihren Stand beim Wechsel.
 * Mit `lazy` wird ein Inhalt erst beim ersten Oeffnen gemountet (Videos laden nichts, solange
 * niemand dort war) und bleibt danach gemountet.
 *
 * Die Reiterleiste klebt am oberen Rand des scrollenden Dialoginhalts (`data-dialog-body`) und
 * scrollt am Handy waagerecht, ohne die Seite zu verbreitern. Ihre Hoehe steht im Inhalt als CSS-Variable
 * `--tab-bar-h` bereit: Was darunter ebenfalls klebt (das Feld des Skizzeneditors bei einer Auswahl) setzt
 * seinen oberen Abstand darauf und verdeckt die Leiste nicht. Anders als `Tabs` in `ui.tsx`
 * (Navigation ohne Inhaltsbereiche) gehoert zu jedem Reiter hier ein Inhaltsbereich.
 */

export interface TabPanelItem {
  id: string;
  label: string;
  content: ReactNode;
  /** Der Reiter enthaelt Fehler: kleiner Punkt, fuer Screenreader mit Text. */
  invalid?: boolean;
  /** Gedaempft dargestellt (noch nichts zu tun), aber waehlbar. */
  muted?: boolean;
  /** Inhalt erst beim ersten Oeffnen mounten. */
  lazy?: boolean;
  /** Der Inhaltsbereich laeuft bis an den Rand des Dialogs (am Handy ohne seitlichen Rand). */
  bleed?: boolean;
}

export function TabPanels({
  tabs,
  active,
  onChange,
  label,
  notice,
}: {
  tabs: TabPanelItem[];
  active: string;
  onChange: (id: string) => void;
  /** Name der Reiterleiste fuer Screenreader. */
  label: string;
  /** Steht unter der Leiste, ueber den Inhalten, auf jedem Reiter (z. B. eine Fehlermeldung). */
  notice?: ReactNode;
}): JSX.Element {
  const base = useId();
  const root = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState<Set<string>>(() => new Set([active]));
  const tabId = (id: string): string => `${base}-tab-${id}`;
  const panelId = (id: string): string => `${base}-panel-${id}`;

  // Auch ein von aussen gewaehlter Reiter (Fehler beim Speichern) zaehlt als geoeffnet.
  useEffect(() => {
    setOpened((current) => (current.has(active) ? current : new Set(current).add(active)));
  }, [active]);

  // Hoehe der klebenden Leiste als CSS-Variable, damit weitere klebende Teile darunter einrasten.
  const bar = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useLayoutEffect(() => {
    const element = bar.current;
    if (!element) return;
    const measure = (): void => setBarHeight(Math.round(element.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Scrollt der Browser etwas ins Bild (Fokus, Tastatur), soll es unter der klebenden Leiste landen, nicht dahinter.
  useLayoutEffect(() => {
    const body = root.current?.closest<HTMLElement>('[data-dialog-body]');
    if (!body || barHeight === 0) return;
    body.style.scrollPaddingTop = `${barHeight}px`;
    return () => {
      body.style.scrollPaddingTop = '';
    };
  }, [barHeight]);

  // Ein anderer Reiter beginnt oben, nicht dort, wo der vorige gescrollt war.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    root.current?.closest('[data-dialog-body]')?.scrollTo?.({ top: 0 });
  }, [active]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    const target = tabs[next]!;
    onChange(target.id);
    document.getElementById(tabId(target.id))?.focus();
  };

  return (
    // Die Wurzel zieht in den Innenrand des Dialoginhalts hinauf, damit die klebende Leiste ganz oben sitzt.
    <div ref={root} className="-mt-4" style={barHeight > 0 ? ({ '--tab-bar-h': `${barHeight}px` } as CSSProperties) : undefined}>
      <div ref={bar} className="sticky top-0 z-10 -mx-4 bg-white px-4 pb-3 pt-1">
        <div role="tablist" aria-label={label} className="flex overflow-x-auto border-b border-slate-200 [scrollbar-width:none]">
          {tabs.map((tab, index) => {
            const selected = tab.id === active;
            return (
              <button
                key={tab.id}
                id={tabId(tab.id)}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={panelId(tab.id)}
                tabIndex={selected ? 0 : -1}
                onClick={() => onChange(tab.id)}
                onKeyDown={(event) => onKeyDown(event, index)}
                className={cn(
                  '-mb-px flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors sm:min-h-0',
                  selected
                    ? 'border-brand-600 font-semibold text-brand-700'
                    : cn('border-transparent font-medium hover:text-slate-700', tab.muted ? 'text-slate-400' : 'text-slate-600'),
                )}
              >
                {tab.label}
                {tab.invalid && (
                  <>
                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-rose-600" />
                    <span className="sr-only">, enthält Fehler</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {notice}
      {tabs.map((tab) => (
        <div
          key={tab.id}
          id={panelId(tab.id)}
          role="tabpanel"
          aria-labelledby={tabId(tab.id)}
          hidden={tab.id !== active}
          className={cn(tab.bleed && '-mx-4 sm:mx-0')}
        >
          {(!tab.lazy || opened.has(tab.id)) && tab.content}
        </div>
      ))}
    </div>
  );
}
